const graphemes = new Intl.Segmenter('en', { granularity: 'grapheme' });

/**
 * Page icons are drawn from the bundled icon set by name. The set is grouped so the
 * picker can offer one drawer at a time instead of a wall of 871 glyphs; the groups
 * are the catalogue itself, and `noteIconNames` is the flattened list every validator
 * uses. Notes saved before the set existed hold a single emoji instead, so both forms
 * stay valid and older notes keep their icon without a migration.
 */
export const noteIconGroups = [
  {
    name: 'documents',
    icons: [
      'archive',
      'archive-restore',
      'book',
      'book-a',
      'book-audio',
      'book-copy',
      'book-headphones',
      'book-heart',
      'book-image',
      'book-key',
      'book-lock',
      'book-marked',
      'book-open',
      'book-open-text',
      'book-text',
      'book-type',
      'book-user',
      'bookmark',
      'clipboard',
      'clipboard-check',
      'clipboard-clock',
      'clipboard-copy',
      'clipboard-list',
      'clipboard-paste',
      'clipboard-pen',
      'clipboard-type',
      'eraser',
      'file',
      'file-archive',
      'file-box',
      'file-braces',
      'file-chart-column',
      'file-chart-column-increasing',
      'file-chart-line',
      'file-chart-pie',
      'file-check',
      'file-clock',
      'file-code',
      'file-digit',
      'file-headphone',
      'file-heart',
      'file-image',
      'file-key',
      'file-lock',
      'file-music',
      'file-pen',
      'file-play',
      'file-signal',
      'file-sliders',
      'file-spreadsheet',
      'file-stack',
      'file-terminal',
      'file-text',
      'file-type',
      'file-user',
      'file-video-camera',
      'file-volume',
      'files',
      'folder',
      'folder-archive',
      'folder-bookmark',
      'folder-clock',
      'folder-code',
      'folder-git',
      'folder-heart',
      'folder-kanban',
      'folder-key',
      'folder-lock',
      'folder-open',
      'folder-tree',
      'folders',
      'highlighter',
      'inbox',
      'kanban',
      'library',
      'list',
      'list-check',
      'list-checks',
      'list-clock',
      'list-music',
      'list-todo',
      'newspaper',
      'notebook',
      'notebook-pen',
      'notebook-tabs',
      'notebook-text',
      'notepad-text',
      'paperclip',
      'pen',
      'pen-line',
      'pen-tool',
      'pencil',
      'pencil-line',
      'pencil-ruler',
      'pencil-sparkles',
      'printer',
      'quote',
      'scroll',
      'scroll-text',
      'square-kanban',
      'square-library',
      'square-pen',
      'stamp',
      'sticky-note',
      'sticky-notes',
      'table',
      'table-2',
      'table-of-contents',
      'table-properties',
      'tag',
      'tags',
    ],
  },
  {
    name: 'work',
    icons: [
      'activity',
      'alarm-clock',
      'briefcase',
      'briefcase-business',
      'briefcase-conveyor-belt',
      'briefcase-medical',
      'calculator',
      'calendar',
      'calendar-check',
      'calendar-clock',
      'calendar-days',
      'calendar-fold',
      'calendar-heart',
      'calendar-range',
      'calendars',
      'chart-area',
      'chart-bar',
      'chart-candlestick',
      'chart-column',
      'chart-gantt',
      'chart-line',
      'chart-network',
      'chart-pie',
      'chart-scatter',
      'chart-spline',
      'check',
      'circle-check',
      'clock',
      'cog',
      'crosshair',
      'download',
      'drafting-compass',
      'flag',
      'flag-triangle-left',
      'flag-triangle-right',
      'funnel',
      'gauge',
      'gavel',
      'goal',
      'handshake',
      'history',
      'hourglass',
      'lectern',
      'milestone',
      'podium',
      'presentation',
      'refresh-ccw',
      'refresh-cw',
      'repeat',
      'rocket',
      'rotate-cw',
      'save',
      'scale',
      'search',
      'search-check',
      'send',
      'send-horizontal',
      'settings',
      'share',
      'share-2',
      'sliders-horizontal',
      'sliders-vertical',
      'square-check',
      'target',
      'timeline',
      'timer',
      'timer-reset',
      'trending-down',
      'trending-up',
      'upload',
      'vote',
      'workflow',
      'zap',
    ],
  },
  {
    name: 'people',
    icons: [
      'accessibility',
      'at-sign',
      'baby',
      'balloon',
      'bell',
      'bell-electric',
      'bell-off',
      'bell-ring',
      'bot-message-square',
      'contact',
      'face-angry',
      'face-expressionless',
      'face-grinning',
      'face-neutral',
      'face-slightly-frowning',
      'face-slightly-smiling',
      'footprints',
      'hand',
      'hand-fist',
      'hand-grab',
      'hand-heart',
      'hand-platter',
      'heart',
      'heart-crack',
      'heart-handshake',
      'id-card',
      'id-card-lanyard',
      'mail',
      'mail-check',
      'mail-clock',
      'mail-open',
      'mailbox',
      'mails',
      'mars',
      'mars-stroke',
      'megaphone',
      'message-circle',
      'message-circle-code',
      'message-circle-heart',
      'message-square',
      'message-square-code',
      'message-square-heart',
      'message-square-lock',
      'message-square-quote',
      'message-square-share',
      'message-square-text',
      'messages-square',
      'party-popper',
      'person-standing',
      'phone',
      'phone-call',
      'reply',
      'rss',
      'smile',
      'speech',
      'transgender',
      'user',
      'user-check',
      'user-key',
      'user-lock',
      'user-plus',
      'user-round',
      'user-shield',
      'user-star',
      'users',
      'venus',
      'venus-and-mars',
      'voicemail',
    ],
  },
  {
    name: 'tech',
    icons: [
      'airplay',
      'antenna',
      'app-window',
      'app-window-mac',
      'barcode',
      'battery',
      'battery-charging',
      'battery-full',
      'bluetooth',
      'bluetooth-connected',
      'bluetooth-searching',
      'bot',
      'cable',
      'card-sim',
      'cast',
      'circuit-board',
      'computer',
      'cpu',
      'disc',
      'ethernet-port',
      'fingerprint-pattern',
      'gpu',
      'hard-drive',
      'hdmi-port',
      'headset',
      'keyboard',
      'laptop',
      'laptop-minimal',
      'memory-stick',
      'microchip',
      'midi-port',
      'monitor',
      'monitor-cloud',
      'monitor-off',
      'monitor-pause',
      'monitor-play',
      'monitor-smartphone',
      'monitor-speaker',
      'monitor-stop',
      'mouse',
      'mouse-left',
      'mouse-right',
      'network',
      'nfc',
      'pc-case',
      'plug',
      'plug-zap',
      'power',
      'projector',
      'qr-code',
      'radio-tower',
      'robot-arm',
      'robot-vacuum',
      'router',
      'satellite',
      'satellite-dish',
      'scan',
      'screen-share',
      'server',
      'server-crash',
      'signal',
      'smartphone',
      'smartphone-charging',
      'smartphone-nfc',
      'tablet',
      'tablet-smartphone',
      'touchpad',
      'tv',
      'tv-minimal',
      'tv-minimal-play',
      'unplug',
      'usb',
      'usb-c-port',
      'vibrate',
      'videotape',
      'watch',
      'webcam',
      'wifi',
      'wifi-off',
    ],
  },
  {
    name: 'code',
    icons: [
      'binary',
      'blocks',
      'box',
      'boxes',
      'braces',
      'brackets',
      'brain-circuit',
      'bug',
      'bug-play',
      'code',
      'code-xml',
      'command',
      'component',
      'container',
      'database',
      'database-zap',
      'git-branch',
      'git-commit-horizontal',
      'git-commit-vertical',
      'git-compare',
      'git-fork',
      'git-graph',
      'git-merge',
      'git-pull-request',
      'layers',
      'parentheses',
      'pipette',
      'regex',
      'search-code',
      'square-terminal',
      'terminal',
      'variable',
      'webhook',
    ],
  },
  {
    name: 'media',
    icons: [
      'aperture',
      'audio-lines',
      'audio-waveform',
      'boom-box',
      'brush',
      'brush-cleaning',
      'camera',
      'captions',
      'cassette-tape',
      'clapperboard',
      'closed-caption',
      'disc-album',
      'drama',
      'drum',
      'film',
      'guitar',
      'headphones',
      'image',
      'image-play',
      'image-upscale',
      'images',
      'keyboard-music',
      'languages',
      'lens-concave',
      'lens-convex',
      'ligature',
      'metronome',
      'mic',
      'mic-off',
      'mic-vocal',
      'music',
      'paint-bucket',
      'paint-roller',
      'paintbrush',
      'paintbrush-vertical',
      'palette',
      'piano',
      'radio',
      'radio-receiver',
      'sparkle',
      'sparkles',
      'speaker',
      'spotlight',
      'spray-can',
      'sticker',
      'swatch-book',
      'theater',
      'turntable',
      'venetian-mask',
      'video',
      'video-off',
      'volume',
      'volume-off',
      'wand',
      'wand-sparkles',
    ],
  },
  {
    name: 'tools',
    icons: [
      'anvil',
      'axe',
      'backpack',
      'bolt',
      'bomb',
      'construction',
      'drill',
      'flashlight',
      'forklift',
      'glasses',
      'hammer',
      'hard-hat',
      'hat-glasses',
      'key',
      'key-round',
      'key-square',
      'link',
      'link-2',
      'lock',
      'lock-keyhole',
      'lock-keyhole-open',
      'lock-open',
      'pickaxe',
      'pin',
      'pocket-knife',
      'recycle',
      'scissors',
      'shield',
      'shield-check',
      'shield-keyhole',
      'shield-lock',
      'shield-user',
      'shirt',
      'shovel',
      'shredder',
      'tool-case',
      'toolbox',
      'tractor',
      'trash',
      'umbrella',
      'unlock',
      'wrench',
    ],
  },
  {
    name: 'money',
    icons: [
      'banknote',
      'bitcoin',
      'coins',
      'credit-card',
      'credit-card-reader',
      'currency',
      'diamond-percent',
      'dollar-sign',
      'euro',
      'gem',
      'georgian-lari',
      'gift',
      'hand-coins',
      'handbag',
      'indian-rupee',
      'japanese-yen',
      'package',
      'package-2',
      'package-check',
      'package-open',
      'paper-bag',
      'percent',
      'philippine-peso',
      'piggy-bank',
      'pound-sterling',
      'receipt',
      'receipt-text',
      'russian-ruble',
      'saudi-riyal',
      'shopping-bag',
      'shopping-basket',
      'shopping-cart',
      'swiss-franc',
      'turkish-lira',
      'vault',
      'wallet',
      'wallet-cards',
      'wallet-minimal',
    ],
  },
  {
    name: 'places',
    icons: [
      'air-vent',
      'alarm-smoke',
      'armchair',
      'bath',
      'bed',
      'bed-double',
      'bed-single',
      'blinds',
      'brick-wall',
      'brick-wall-fire',
      'brick-wall-shield',
      'bridge',
      'broom',
      'broom-sparkles',
      'building',
      'building-2',
      'castle',
      'cctv',
      'church',
      'concierge-bell',
      'dam',
      'door-closed-locked',
      'door-open',
      'door-stairwell',
      'factory',
      'fan',
      'fence',
      'fire-extinguisher',
      'heater',
      'hospital',
      'house',
      'house-heart',
      'house-plug',
      'house-wifi',
      'inspection-panel',
      'lamp',
      'lamp-ceiling',
      'lamp-desk',
      'lamp-floor',
      'landmark',
      'lighthouse',
      'microwave',
      'mirror-rectangular',
      'mop',
      'mop-sparkles',
      'mosque',
      'refrigerator',
      'rocking-chair',
      'school',
      'shelving-unit',
      'shower-head',
      'siren',
      'soap-dispenser-droplet',
      'sofa',
      'solar-panel',
      'store',
      'toilet',
      'towel-rack',
      'university',
      'wallpaper',
      'warehouse',
      'washing-machine',
    ],
  },
  {
    name: 'travel',
    icons: [
      'anchor',
      'baggage-claim',
      'bike',
      'binoculars',
      'bus',
      'bus-front',
      'cable-car',
      'car',
      'car-battery',
      'car-front',
      'car-taxi-front',
      'caravan',
      'compass',
      'drone',
      'engine',
      'ev-charger',
      'ferris-wheel',
      'fuel',
      'helicopter',
      'hotel',
      'kayak',
      'life-buoy',
      'luggage',
      'map',
      'map-pin',
      'map-pin-house',
      'map-pinned',
      'motorbike',
      'navigation',
      'parasol',
      'parking-meter',
      'plane',
      'plane-landing',
      'plane-takeoff',
      'radar',
      'road',
      'roller-coaster',
      'route',
      'sailboat',
      'scooter',
      'ship',
      'ship-cargo',
      'ship-wheel',
      'signpost',
      'tent',
      'tent-tree',
      'ticket',
      'ticket-percent',
      'tickets',
      'tickets-plane',
      'tower-control',
      'traffic-cone',
      'trailer',
      'train-front',
      'train-front-tunnel',
      'train-track',
      'tram-front',
      'truck',
      'truck-electric',
      'utility-pole',
      'van',
      'waypoints',
    ],
  },
  {
    name: 'nature',
    icons: [
      'bubbles',
      'cannabis',
      'cloud',
      'cloud-download',
      'cloud-drizzle',
      'cloud-fog',
      'cloud-hail',
      'cloud-lightning',
      'cloud-moon',
      'cloud-moon-rain',
      'cloud-off',
      'cloud-rain',
      'cloud-rain-wind',
      'cloud-snow',
      'cloud-sun',
      'cloud-sun-rain',
      'cloud-upload',
      'cloudy',
      'clover',
      'droplet',
      'droplets',
      'earth',
      'earth-lock',
      'eclipse',
      'feather',
      'flame',
      'flame-kindling',
      'flower',
      'flower-2',
      'galaxy',
      'globe',
      'globe-lock',
      'haze',
      'hop',
      'land-plot',
      'leaf',
      'leafy-green',
      'moon',
      'moon-star',
      'mountain',
      'mountain-snow',
      'orbit',
      'rainbow',
      'rose',
      'shell',
      'shrub',
      'snowflake',
      'sprout',
      'star',
      'sun',
      'sun-moon',
      'sun-snow',
      'sunrise',
      'sunset',
      'thermometer-snowflake',
      'thermometer-sun',
      'tornado',
      'tree-deciduous',
      'tree-palm',
      'tree-pine',
      'trees',
      'waves-horizontal',
      'waves-ladder',
      'waves-vertical',
      'wheat',
      'wind',
    ],
  },
  {
    name: 'animals',
    icons: [
      'bird',
      'birdhouse',
      'bone',
      'cat',
      'dog',
      'fish',
      'fish-symbol',
      'fishing-hook',
      'fishing-rod',
      'panda',
      'paw-print',
      'rabbit',
      'rat',
      'shrimp',
      'snail',
      'squirrel',
      'turtle',
      'worm',
    ],
  },
  {
    name: 'food',
    icons: [
      'apple',
      'banana',
      'barrel',
      'bean',
      'beef',
      'beer',
      'blender',
      'bottle-wine',
      'broccoli',
      'cake',
      'cake-slice',
      'can',
      'can-soda',
      'candy',
      'candy-cane',
      'carrot',
      'chef-hat',
      'cherry',
      'citrus',
      'coffee',
      'cookie',
      'cooking-pot',
      'croissant',
      'cup-soda',
      'dessert',
      'donut',
      'drumstick',
      'egg',
      'egg-fried',
      'glass-water',
      'grape',
      'ham',
      'hamburger',
      'ice-cream-bowl',
      'ice-cream-cone',
      'lollipop',
      'martini',
      'milk',
      'nut',
      'pizza',
      'popcorn',
      'popsicle',
      'salad',
      'sandwich',
      'soup',
      'utensils',
      'utensils-crossed',
      'vegan',
      'wine',
    ],
  },
  {
    name: 'health',
    icons: [
      'ambulance',
      'bandage',
      'biceps-flexed',
      'biohazard',
      'bone-fracture',
      'brain',
      'cigarette',
      'cross',
      'dumbbell',
      'ear',
      'eye',
      'eye-off',
      'germ',
      'heart-pulse',
      'pill',
      'pill-bottle',
      'radiation',
      'stethoscope',
      'syringe',
      'tablets',
      'thermometer',
      'virus',
      'weight',
    ],
  },
  {
    name: 'sports',
    icons: [
      'award',
      'bow-arrow',
      'chess-bishop',
      'chess-king',
      'chess-knight',
      'chess-pawn',
      'chess-queen',
      'chess-rook',
      'club',
      'crown',
      'dices',
      'gamepad',
      'gamepad-2',
      'gamepad-directional',
      'joystick',
      'medal',
      'playing-card',
      'playing-cards',
      'playing-cards-fan',
      'puzzle',
      'ribbon',
      'spade',
      'sport-shoe',
      'sword',
      'swords',
      'toy-brick',
      'trophy',
      'volleyball',
      'whistle',
    ],
  },
  {
    name: 'science',
    icons: [
      'astroid',
      'atom',
      'beaker',
      'cone',
      'cuboid',
      'cylinder',
      'dna',
      'flask-conical',
      'graduation-cap',
      'infinity',
      'lightbulb',
      'lightbulb-off',
      'magnet',
      'microscope',
      'omega',
      'pentagon',
      'phi',
      'pi',
      'pyramid',
      'radical',
      'ruler',
      'ruler-dimension-line',
      'sigma',
      'spline',
      'telescope',
      'test-tube',
      'test-tube-diagonal',
      'test-tubes',
      'torus',
    ],
  },
  {
    name: 'symbols',
    icons: [
      'badge',
      'badge-check',
      'circle',
      'circle-alert',
      'circle-user',
      'copyleft',
      'copyright',
      'creative-commons',
      'diamond',
      'external-link',
      'ghost',
      'hash',
      'help-circle',
      'hexagon',
      'info',
      'origami',
      'shapes',
      'skull',
      'square',
      'triangle',
      'triangle-right',
      'zodiac-aquarius',
      'zodiac-aries',
      'zodiac-cancer',
      'zodiac-capricorn',
      'zodiac-gemini',
      'zodiac-leo',
      'zodiac-libra',
      'zodiac-ophiuchus',
      'zodiac-pisces',
      'zodiac-sagittarius',
      'zodiac-scorpio',
      'zodiac-taurus',
      'zodiac-virgo',
    ],
  },
] as const;

export type NoteIconGroupName = (typeof noteIconGroups)[number]['name'];

export type NoteIconName = (typeof noteIconGroups)[number]['icons'][number];

export const noteIconNames: readonly NoteIconName[] = noteIconGroups.flatMap(group => group.icons);

/** Backgrounds reuse the semantic tones; page icons never introduce their own colors. */
export const noteIconTones = [
  'neutral',
  'priority',
  'info',
  'focus',
  'success',
  'attention',
] as const;

export type NoteIconTone = (typeof noteIconTones)[number];

export function isNoteIconName(value: string): value is NoteIconName {
  return (noteIconNames as readonly string[]).includes(value);
}

/** One emoji, including flags, skin tones and joined family/profession sequences. */
export function isLegacyNoteEmoji(value: string) {
  return (
    value.length > 0 &&
    value.length <= 32 &&
    [...graphemes.segment(value)].length === 1 &&
    // biome-ignore lint/suspicious/noMisleadingCharacterClass: the class deliberately accepts the U+20E3 keycap combining mark as an emoji signal (for example 1️⃣)
    /[\p{Extended_Pictographic}\p{Regional_Indicator}\u20e3]/u.test(value)
  );
}

export function isNoteIcon(value: string) {
  return isNoteIconName(value) || isLegacyNoteEmoji(value);
}

export function isNoteIconTone(value: string): value is NoteIconTone {
  return (noteIconTones as readonly string[]).includes(value);
}
